import {
  endOfMonth,
  endOfYear,
  isSameDay,
  isSameMonth,
  isSameYear,
  startOfMonth,
  startOfYear,
  type CalendarDate,
  today,
} from '@internationalized/date';
import { useContext, useMemo, useState } from 'react';
import { useDateFormatter } from 'react-aria';
import { CalendarStateContext as RACCalendarStateContext } from 'react-aria-components';
import { useLocalizedMessages } from '../../../../i18n/index.js';
import { constrainValue, getEraFormat, isDateInvalid } from '../../utils.js';
import type { ViewGridCellData, ViewGridState } from './../types.js';
import { useDatePickerView } from './../view-provider.js';

const ITEMS_PER_ROW = 3;
const YEARS_SKIP_AMOUNT = 10;

/** @internal */
export function useYearViewState(): ViewGridState {
  const calendarState = useContext(RACCalendarStateContext)!;
  const { switchToView, focusedDate, setFocusedDate } = useDatePickerView();
  const messages = useLocalizedMessages('datePicker');
  const [isFocused, setIsFocused] = useState(true);

  const currentDate = useMemo(() => today(calendarState.timeZone), [calendarState.timeZone]);

  const monthFormatter = useDateFormatter({
    month: 'short',
    calendar: focusedDate.calendar.identifier,
    timeZone: calendarState.timeZone,
  });

  const monthAriaLabelFormatter = useDateFormatter({
    month: 'long',
    year: 'numeric',
    calendar: focusedDate.calendar.identifier,
    timeZone: calendarState.timeZone,
  });

  const yearFormatter = useDateFormatter({
    year: 'numeric',
    era: getEraFormat(focusedDate),
    calendar: focusedDate.calendar.identifier,
    timeZone: calendarState.timeZone,
  });

  // Some calendars have more than 12 months, and in some calendars the number of months varies per year.
  // This can result in partially filled grid rows.
  // Detect this to keep keyboard navigation between rows consistent.
  const monthCount = focusedDate.calendar.getMonthsInYear(focusedDate);
  const previousYearMonthCount = focusedDate.calendar.getMonthsInYear(focusedDate.subtract({ years: 1 }));
  const partialRowMonthCount = monthCount % ITEMS_PER_ROW;
  const previousYearPartialRowMonthCount = previousYearMonthCount % ITEMS_PER_ROW;

  const data: (ViewGridCellData | null)[][] = useMemo(() => {
    const rowCount = Math.ceil(monthCount / ITEMS_PER_ROW);
    const result: (ViewGridCellData | null)[][] = new Array(rowCount);

    for (let rowIdx = 0; rowIdx < rowCount; rowIdx++) {
      result[rowIdx] = new Array(ITEMS_PER_ROW).fill(null);
    }

    for (let monthIdx = 0; monthIdx < monthCount; monthIdx++) {
      const rowIdx = Math.floor(monthIdx / ITEMS_PER_ROW);
      const colIdx = monthIdx % ITEMS_PER_ROW;

      const date = focusedDate.set({ month: monthIdx + 1 });
      const nativeDate = date.toDate(calendarState.timeZone);

      result[rowIdx]![colIdx] = {
        date,
        content: monthFormatter.format(nativeDate),
        ariaLabel: monthAriaLabelFormatter.format(nativeDate),
        isCurrent: isSameYear(date, currentDate) && isSameMonth(date, currentDate),
      };
    }

    return result;
  }, [monthCount, calendarState.timeZone, focusedDate, monthFormatter, monthAriaLabelFormatter, currentDate]);

  const focusCell = (date: CalendarDate) => {
    setFocusedDate(constrainValue(date, calendarState.minValue, calendarState.maxValue));
  };

  return {
    header: yearFormatter.format(focusedDate.toDate(calendarState.timeZone)),
    previousLabel: messages.format('previousYear'),
    nextLabel: messages.format('nextYear'),
    data,
    isReadOnly: calendarState.isReadOnly,
    isDisabled: calendarState.isDisabled,
    isFocused,
    setIsFocused,
    focusedDate,
    setFocusedDate,
    timeZone: calendarState.timeZone,
    focusPreviousItem() {
      focusCell(focusedDate.subtract({ months: 1 }));
    },
    focusNextItem() {
      focusCell(focusedDate.add({ months: 1 }));
    },
    focusPreviousRow() {
      const currentMonth = focusedDate.month;
      let monthsToSubtract: number;

      if (currentMonth <= previousYearPartialRowMonthCount) {
        monthsToSubtract = previousYearPartialRowMonthCount;
      } else if (currentMonth <= ITEMS_PER_ROW) {
        monthsToSubtract = ITEMS_PER_ROW + previousYearPartialRowMonthCount;
      } else {
        monthsToSubtract = ITEMS_PER_ROW;
      }

      focusCell(
        focusedDate.subtract({
          months: monthsToSubtract,
        }),
      );
    },
    focusNextRow() {
      const currentMonth = focusedDate.month;
      let monthsToAdd: number;

      if (currentMonth > monthCount - partialRowMonthCount) {
        monthsToAdd = partialRowMonthCount;
      } else if (currentMonth + ITEMS_PER_ROW > monthCount) {
        monthsToAdd = ITEMS_PER_ROW + partialRowMonthCount;
      } else {
        monthsToAdd = ITEMS_PER_ROW;
      }

      focusCell(
        focusedDate.add({
          months: monthsToAdd,
        }),
      );
    },
    focusSectionStart() {
      focusCell(startOfYear(focusedDate).set({ day: focusedDate.day }));
    },
    focusSectionEnd() {
      focusCell(endOfYear(focusedDate).set({ day: focusedDate.day }));
    },
    focusPreviousSection(shouldSkip) {
      focusCell(focusedDate.subtract({ years: shouldSkip ? YEARS_SKIP_AMOUNT : 1 }));
    },
    focusNextSection(shouldSkip) {
      focusCell(focusedDate.add({ years: shouldSkip ? YEARS_SKIP_AMOUNT : 1 }));
    },
    isPreviousSectionInvalid() {
      const current = startOfYear(focusedDate);
      const prev = current.subtract({ days: 1 });

      return (
        isDateInvalid(current, calendarState.minValue, calendarState.maxValue) ||
        isDateInvalid(prev, calendarState.minValue, calendarState.maxValue) ||
        isSameDay(prev, current)
      );
    },
    isNextSectionInvalid() {
      const current = endOfYear(focusedDate);
      const next = current.add({ days: 1 });

      return (
        isDateInvalid(current, calendarState.minValue, calendarState.maxValue) ||
        isDateInvalid(next, calendarState.minValue, calendarState.maxValue) ||
        isSameDay(next, current)
      );
    },
    selectFocusedDate() {
      switchToView('month');
    },
    isCellDisabled(date) {
      return (
        isDateInvalid(endOfMonth(date), calendarState.minValue, null) ||
        isDateInvalid(startOfMonth(date), null, calendarState.maxValue)
      );
    },
    isCellSelected(date) {
      return (
        calendarState.value !== null && isSameYear(date, calendarState.value) && isSameMonth(date, calendarState.value)
      );
    },
    isCellTabbable(date) {
      return isSameYear(date, focusedDate) && isSameMonth(date, focusedDate);
    },
  };
}
